import type { CatalogEntry } from '../../../api/admin';
import type { ConnectionRow, WorkflowGraph, WorkflowNode } from '../../../api/forms';
import { Modal } from '../../../components/fs';
import { branchExits, edgesOutOf, portsOf, refuseEdge } from './graph';

/**
 * Joining two steps without dragging.
 *
 * Drag-from-a-port is the good gesture and stays the primary one. But it is
 * the only one, and an 11px circle is a poor target for a trackpad and an
 * impossible one for somebody working by keyboard — so this is the same rule
 * reached a different way, not a second rule.
 *
 * It offers exactly what a drag would ACCEPT: every target `refuseEdge` does
 * not refuse. A step that is missing from this list is a step the drag would
 * have turned away too, and the reason it gives is the same sentence.
 */
export default function ConnectModal({
  graph, node, connections, catalog, onConnect, onClose, direction = 'after',
}: {
  graph: WorkflowGraph;
  node: WorkflowNode;
  connections: ConnectionRow[];
  catalog: CatalogEntry[];
  onConnect: (from: string, port: string, to: string) => void;
  onClose: () => void;
  /**
   * Which end of the arrow is being chosen (§4H H4).
   *
   * `'after'` is the original: this step's free outputs, and what each could
   * reach. `'before'` is the reverse — every step with a free output that
   * could legally reach THIS one — which is the only way to wire in an
   * orphaned step without a drag.
   */
  direction?: 'after' | 'before';
}) {
  const connById = new Map(connections.map((c) => [c.id, c]));
  const specOf = (type?: string) => catalog.find((c) => c.type === type);

  const nameOf = (n: WorkflowNode): string => {
    if (n.kind === 'start') return 'the submission arriving';
    if (n.kind === 'merge') return '↳ the paths rejoining';
    if (n.kind === 'branch') return `? ${String(n.config?.name || 'a branch')}`;
    if (n.kind === 'end') {
      return `⚑ ${n.config?.mode === 'continue' ? 'Into the follow-up form' : String(n.config?.title || 'an ending')}`;
    }
    const conn = n.connectionId ? connById.get(n.connectionId) : undefined;
    return `${specOf(conn?.type)?.icon || '•'} ${conn?.name || n.kind}`;
  };

  const labelFor = (n: WorkflowNode, port: string) => {
    if (n.kind !== 'branch') return null;
    const exit = branchExits(n).find((x) => x.port === port);
    // A further path's port is a generated name nobody has seen, so it is
    // never the fallback; its place is.
    return exit?.label || (port === 'yes' || port === 'no' ? port : exit?.fallback || port);
  };

  /**
   * The rows, either way round. `refuseEdge` decides both: a source and a port
   * that could reach this step is the same question asked from the other end,
   * so nothing here knows a rule the drag does not.
   */
  const options: { port: string; label: string | null; from?: WorkflowNode; targets: WorkflowNode[] }[] =
    direction === 'after'
      ? portsOf(node)
        .filter((port) => !edgesOutOf(graph, node.id, port).length)
        .map((port) => ({
          port,
          label: labelFor(node, port),
          targets: graph.nodes.filter((t) => !refuseEdge(graph, node.id, port, t.id)),
        }))
      : graph.nodes.flatMap((src) => portsOf(src)
        .filter((port) => !edgesOutOf(graph, src.id, port).length)
        .filter((port) => !refuseEdge(graph, src.id, port, node.id))
        .map((port) => ({ port, label: labelFor(src, port), from: src, targets: [src] })));

  const nothing = options.every((o) => !o.targets.length);

  return (
    <Modal
      title={direction === 'after' ? `What runs after ${nameOf(node)}?` : `What runs before ${nameOf(node)}?`}
      aside="the same joins a drag would allow"
      onClose={onClose}
      footer={<button className="fs-btn neutral" onClick={onClose}>Cancel</button>}
    >
      {!options.length && (
        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          {direction === 'before'
            ? 'Nothing could lead here — every other step either already leads somewhere, or is below this one.'
            : portsOf(node).length
              ? 'Every output of this step already leads somewhere. Remove an arrow first.'
              : 'An ending is where a path stops, so nothing runs after it.'}
        </p>
      )}
      {nothing && !!options.length && (
        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          There is nothing this could lead to yet — every other step either already has something
          before it, or is above this one. Add a step, or put a “paths rejoin” below.
        </p>
      )}
      {options.map(({ port, label, targets, from }) => (
        targets.length > 0 && (
          <div key={`${from ? from.id : ''}:${port}`} style={{ marginBottom: 14 }}>
            {label && (
              <div className="wf-rail-group" style={{ marginBottom: 6 }}>
                On the “{label}” path
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {targets.map((t) => (
                <button
                  key={t.id}
                  className="wf-rail-entry wf-connect-row"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    // Whichever way the list was read, the arrow is stored the
                    // one way it exists: from a source's port to a target.
                    if (direction === 'before') onConnect(t.id, port, node.id);
                    else onConnect(node.id, port, t.id);
                    onClose();
                  }}
                >
                  {nameOf(t)}
                </button>
              ))}
            </div>
          </div>
        )
      ))}
    </Modal>
  );
}
